import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';

/**
 * The result of `analyzeOutcome` — a structural-delta summary of what changed
 * between the pre-session and post-session ADF documents, classified by whether
 * the change happened inside or outside the model's intended target region.
 *
 * Consumed by the `success_but_suspicious` outcome-class wiring (CCI-15893)
 * and by downstream dashboards.
 *
 * Notes on `unintendedRegion*` semantics: "intended" here means "explicitly
 * named as a target by one of the tool calls executed during the session"
 * (i.e. present in `affectedLocalIds`). When the backend ships the
 * `selectionLocalIds` field as part of the streaming payload, the analyzer
 * will be upgraded (CCI-15978) to use that as the canonical source of intent
 * and fall back to `affectedLocalIds` only when the backend-supplied field is absent.
 */
export interface OutcomeAnalysis {
	/** All localIds that were explicit tool-call targets during the session. */
	affectedLocalIds: Set<string>;
	/**
	 * True when at least one affected localId (explicit tool-call target) was
	 * present in the pre-doc but absent from the post-doc top-level snapshot.
	 * Indicates that the targeted node was replaced with fresh-localId content
	 * rather than silently dropped. Used to suppress false-positive
	 * `noMeaningfulEffect` (CCI-16968) and `intentEffectMismatch` on structural
	 * expansions (CCI-16969 P2).
	 */
	anyAffectedLocalIdDisappearedPostApply: boolean;
	/**
	 * `true` if every node's (type, user-meaningful attrs, marks) signature
	 * matches between pre and post (multiset equality). `false` if any
	 * attribute-only change occurred (e.g. table-cell background, panel type).
	 * Excludes `localId` and `__`-prefixed attrs. See CCI-16816.
	 */
	attrSignaturesEqual: boolean;
	/**
	 * The session's inferred intent (derived from the mix of tool-call types
	 * actually applied) does not match the **top-level** structural delta.
	 * Top-level (rather than recursive) granularity is used because the
	 * heuristic is about root-shape contradictions, and its `±1` tolerance
	 * was tuned to top-level counts. Examples:
	 * - APPEND intent (only insert / append commands) but the doc lost
	 *   top-level nodes.
	 * - REWRITE intent (predominantly `replaceNode` / `setAttrs`) but the
	 *   top-level node count differs by `≥ 2` (the apply layer added or
	 *   dropped root-level nodes the model didn't ask to touch).
	 * - REMOVE intent (only `removeNode` / `removeNodes`) but the doc
	 *   didn't shrink at the top level.
	 *
	 * Conservative — sessions with mixed or unclassifiable intent never fire
	 * this flag because the heuristic would be too noisy. The signal is
	 * deliberately marked `experimental: true` on the wire because intent is
	 * inferred (not yet supplied by the backend).
	 */
	intentEffectMismatch: boolean;
	/**
	 * The session removed top-level content whose `localId` was outside the
	 * user's selection. Always `false` until the backend ships
	 * `selectionLocalIds` and the FE plumbs it through to this analyzer; see
	 * the follow-up ticket for the heuristic implementation.
	 */
	protectedContentRemoved: boolean;
	/** Top-level nodes present before but absent after. */
	removedNodeCount: number;
	/** Total text content length after minus before. */
	textCharacterDelta: number;
	/** Top-level node count after minus before. Negative => net removal. */
	totalNodeDelta: number;
	/** Top-level node still present after but with text content changed, that
	 * was NOT an explicit target. Only detects modifications to nodes with
	 * a `localId` (the only way to identify the same node across pre/post
	 * snapshots without a deep diff); changes to unlabelled top-level
	 * nodes are not surfaced by this flag.
	 *
	 * A `±1` character tolerance is applied per node to absorb ProseMirror
	 * normalisation artefacts (single trailing whitespace, newline,
	 * punctuation injection/strip on block close), matching the same
	 * tolerance used at the session-wide `noMeaningfulEffect` derivation
	 * site. Per-node deltas of `±1` do not count as "modified".
	 *
	 * Known limitation: text changes are detected by comparing total
	 * character length pre vs post. Same-length rewrites (e.g. "hello" →
	 * "world" in a non-target paragraph) are silent false negatives. A
	 * deeper diff (token / hash) would catch these but is out of scope
	 * for v1; revisit once dashboards exist to measure how often the gap
	 * matters.
	 *
	 * The text-length calculation includes characters from atomic inline
	 * nodes that carry their displayed text in attributes (status, date,
	 * mention, emoji, inlineCard, placeholder). This prevents
	 * text-to-atomic-inline-node conversions (e.g. "Notion" → purple
	 * status lozenge) from being reported as unintended text
	 * modifications. See CCI-16817. */
	unintendedRegionModified: boolean;
	/** Top-level node disappeared between before and after, that was NOT an
	 * explicit target (i.e. nothing in the tool-call list told us this node
	 * was supposed to disappear). */
	unintendedRegionRemoved: boolean;
}

/**
 * A condensed record of one tool call executed during the session. Used to
 * collect `affectedLocalIds` so the analyzer can distinguish intentional
 * removals/modifications from collateral damage.
 */
export interface ToolCallRecord {
	commandType: string;
	targetLocalId?: string;
	targetLocalIds?: string[];
}

/**
 * Top-level ADF / PM JSON document shape. We only need `content` here.
 * Exported so the caller has a one-name cast target for the `toJSON()`
 * outputs of `prosemirror-model` Nodes (whose return type is too loose
 * to satisfy this shape directly).
 */
export type DocLike = JSONDocNode | { content?: unknown[]; type: string };

interface TopLevelNodeRecord {
	localId: string | undefined;
	textLength: number;
}

/**
 * Walk the document once and collect everything the analyzer needs:
 * `byLocalId` (top-level node text-length lookup), `totalNodesTopLevel`,
 * `totalNodesRecursive` (used by `noMeaningfulEffect`), `totalTextLength`,
 * and `attrFingerprints` (multiset for `attrSignaturesEqual`).
 */
function snapshotDoc(doc: DocLike): {
	attrFingerprints: Map<string, number>;
	byLocalId: Map<string, TopLevelNodeRecord>;
	/**
	 * Maps every **nested** node's `localId` → the `localId` of the top-level
	 * node that contains it. Built as a free byproduct of the existing
	 * `collectSubtreeMetrics` walk — no extra document traversal. Used by
	 * `analyzeOutcome` to resolve nested tool-call targets to their top-level
	 * container for `affectedLocalIds` enrichment.
	 */
	nestedToTopLevel: Map<string, string>;
	totalNodesRecursive: number;
	totalNodesTopLevel: number;
	totalTextLength: number;
} {
	const byLocalId = new Map<string, TopLevelNodeRecord>();
	// Multiset (fingerprint → count) — catches compositional swaps with
	// matching unique fingerprints but different multiplicities.
	const attrFingerprints = new Map<string, number>();
	// nested localId → top-level localId, built during the existing walk.
	const nestedToTopLevel = new Map<string, string>();
	let totalNodesTopLevel = 0;
	let totalNodesRecursive = 0;
	let totalTextLength = 0;

	const content = (doc as { content?: unknown[] }).content;
	if (!Array.isArray(content)) {
		return {
			byLocalId,
			totalNodesTopLevel,
			totalNodesRecursive,
			totalTextLength,
			attrFingerprints,
			nestedToTopLevel,
		};
	}

	for (const child of content) {
		if (child === null || typeof child !== 'object') {
			continue;
		}
		totalNodesTopLevel += 1;
		const node = child as { attrs?: { localId?: unknown }; content?: unknown[] };
		const localId = typeof node.attrs?.localId === 'string' ? node.attrs.localId : undefined;
		const { textLength, descendantCount } = collectSubtreeMetrics(
			node,
			attrFingerprints,
			localId,
			nestedToTopLevel,
		);
		// `descendantCount` includes the root node itself.
		totalNodesRecursive += descendantCount;
		totalTextLength += textLength;
		if (localId !== undefined) {
			byLocalId.set(localId, { localId, textLength });
		}
	}

	return {
		byLocalId,
		totalNodesTopLevel,
		totalNodesRecursive,
		totalTextLength,
		attrFingerprints,
		nestedToTopLevel,
	};
}

/**
 * True when `doc` is an "empty" starting document — a single top-level
 * empty paragraph (no child content) and nothing else.
 *
 * The editor seeds a brand-new / fully-cleared page with one placeholder
 * paragraph. That placeholder carries no `localId`, so it counts as an
 * unlabelled top-level node. When `appendNodeToEnd` (or any additive
 * command) writes labelled content into such a page, the apply layer drops
 * the now-redundant placeholder paragraph — a benign, expected side effect,
 * NOT content loss.
 *
 * The unlabelled-node removal accounting in `analyzeOutcome` would otherwise
 * see `postUnlabelledCount (0) < preUnlabelledCount (1)` and raise a
 * false-positive `unintendedRegionRemoved` / `unexpected_content_removal`.
 * See CCI-17328.
 */
function isEmptyStartingDocument(doc: DocLike): boolean {
	const content = (doc as { content?: unknown[] }).content;
	if (!Array.isArray(content) || content.length !== 1) {
		return false;
	}
	const only = content[0];
	if (only === null || typeof only !== 'object') {
		return false;
	}
	const node = only as { content?: unknown[]; type?: unknown };
	if (node.type !== 'paragraph') {
		return false;
	}
	// Empty paragraph: missing or empty `content` array.
	return !Array.isArray(node.content) || node.content.length === 0;
}

/**
 * Attrs excluded from the per-node fingerprint: `localId` (regenerated on
 * every streaming insert) and any `__`-prefixed key (editor-internal / UI-only
 * state, e.g. `__expanded`, `__autoSize`).
 */
function isIgnoredAttr(key: string): boolean {
	return key === 'localId' || key.startsWith('__');
}

/**
 * Stable string fingerprint of a node's identity: `type` + sorted filtered
 * `attrs` + sorted marks. Used by `attrSignaturesEqual`.
 */
function buildNodeAttrFingerprint(node: {
	attrs?: unknown;
	marks?: unknown;
	type?: unknown;
}): string {
	const type = typeof node.type === 'string' ? node.type : '?';
	const attrs = node.attrs;
	let attrsPart = '';
	if (attrs !== null && typeof attrs === 'object') {
		const keys = Object.keys(attrs as Record<string, unknown>)
			.filter((k) => !isIgnoredAttr(k))
			.sort();
		if (keys.length > 0) {
			const pairs: string[] = [];
			for (const key of keys) {
				const value = (attrs as Record<string, unknown>)[key];
				pairs.push(`${key}=${JSON.stringify(value)}`);
			}
			attrsPart = `|${pairs.join(',')}`;
		}
	}
	// Fold marks into the fingerprint (each mark contributes type + attrs).
	const marks = node.marks;
	let marksPart = '';
	if (Array.isArray(marks) && marks.length > 0) {
		const markStrings: string[] = [];
		for (const mark of marks) {
			if (mark !== null && typeof mark === 'object') {
				markStrings.push(buildNodeAttrFingerprint(mark as { attrs?: unknown; type?: unknown }));
			}
		}
		markStrings.sort();
		marksPart = `|marks=${markStrings.join(';')}`;
	}
	return `${type}${attrsPart}${marksPart}`;
}

/**
 * Estimated displayed-text contribution of atomic inline nodes that store
 * their user-visible text in attributes rather than child `text` nodes.
 *
 * Without this accounting the analyzer's `textCharacterDelta` would view
 * a legitimate user-requested conversion (e.g. "Notion" → status lozenge
 * labelled "Notion") as a large negative delta and incorrectly flag the
 * session with `unintendedTextModification`. See CCI-16817.
 *
 * The values returned are intentionally a coarse estimate — the analyzer
 * only consumes them as a `±1` tolerance proxy for "this region's text
 * length changed meaningfully". Sub-character precision is not required.
 *
 * Per-type rationale:
 * - `status`      — `attrs.text` is the lozenge label (e.g. "Notion").
 * - `date`        — rendered as a localised date badge; we have no access
 *                   to the formatter, so use a fixed `10` character estimate
 *                   (matches common short-date renderings like
 *                   "Jan 1, 2024").
 * - `mention`     — `attrs.text` (e.g. "@John Doe"); falls back to `0` if
 *                   absent (mention text is optional in the schema).
 * - `emoji`       — `attrs.text` (the rendered glyph) when present;
 *                   otherwise `attrs.shortName` (e.g. ":smile:").
 * - `inlineCard`  — renders as a link. URL-based cards expose
 *                   `attrs.url`; datasource-based cards (e.g. Jira
 *                   issue inline smart links) expose `attrs.data`
 *                   instead. We prefer `attrs.url` length when
 *                   present, otherwise fall back to
 *                   `JSON.stringify(attrs.data).length` so datasource
 *                   cards also contribute a non-zero text estimate
 *                   and don't re-trigger the
 *                   `unintendedTextModification` false-positive this
 *                   analyzer change is fixing. If neither attr is
 *                   present (malformed / unknown shape) we fall back
 *                   to a fixed `10` char estimate, mirroring `date`.
 * - `placeholder` — `attrs.text` is the placeholder string.
 */
function getAtomicInlineNodeTextLength(
	type: string,
	attrs: Record<string, unknown> | undefined,
): number {
	// `date`'s estimate is independent of attrs content — it must return
	// the fixed value even when attrs is absent or non-object. Handled
	// before the `attrs === undefined` guard so a malformed-ADF `date`
	// node (missing attrs) does not silently fall through to `0` and
	// re-trigger the very false positive this analyzer change is
	// fixing. Short-date renderings are typically ~10 chars
	// ("Jan 1, 2024"); the exact value isn't important — the goal is
	// to absorb the text-delta of the conversion, not to match what
	// the renderer ultimately displays.
	if (type === 'date') {
		return 10;
	}
	if (attrs === undefined) {
		return 0;
	}
	switch (type) {
		case 'status': {
			const text = attrs.text;
			return typeof text === 'string' ? text.length : 0;
		}
		case 'mention': {
			const text = attrs.text;
			return typeof text === 'string' ? text.length : 0;
		}
		case 'emoji': {
			const text = attrs.text;
			if (typeof text === 'string' && text.length > 0) {
				return text.length;
			}
			const shortName = attrs.shortName;
			return typeof shortName === 'string' ? shortName.length : 0;
		}
		case 'inlineCard': {
			const url = attrs.url;
			if (typeof url === 'string' && url.length > 0) {
				return url.length;
			}
			// Datasource-based inline cards (e.g. Jira-issue inline
			// smart links) carry no `url` attr — their content lives
			// in `attrs.data`. Serialise it to JSON to get a coarse
			// non-zero estimate so a text-to-datasource-inlineCard
			// conversion doesn't reintroduce the very false positive
			// CCI-16817 is fixing. The exact length is unimportant —
			// the analyzer only needs a ±1-tolerance proxy.
			const data = attrs.data;
			if (data !== undefined && data !== null) {
				try {
					return JSON.stringify(data).length;
				} catch {
					// Circular refs or unserialisable values fall
					// through to the fixed estimate below.
				}
			}
			// Last-resort fixed estimate — matches the rationale used
			// for `date` (locale-rendered content with no plain text
			// in the ADF). Avoids returning 0 for malformed/unknown
			// inlineCard shapes that would otherwise re-trigger the
			// false positive.
			return 10;
		}
		case 'placeholder': {
			const text = attrs.text;
			return typeof text === 'string' ? text.length : 0;
		}
		default:
			return 0;
	}
}

/**
 * Single recursive walk: returns `textLength` + `descendantCount`,
 * accumulates fingerprints into the passed-in multiset, and — as a free
 * byproduct of the traversal — records every descendant node's `localId`
 * into `nestedToTopLevel` mapped to `topLevelLocalId`. O(N) per snapshot.
 *
 * `textLength` counts characters from both regular `text` nodes AND from
 * atomic inline nodes that store their displayed text in attributes
 * (e.g. `status.text`, `mention.text`). See `getAtomicInlineNodeTextLength`
 * for the per-type contribution and CCI-16817 for the rationale.
 *
 * `topLevelLocalId` is `undefined` for the top-level node itself (which is
 * the ancestor, not a descendant) and for top-level nodes that lack a
 * `localId` (unlabelled nodes can't be looked up anyway). Only descendants
 * of a labelled top-level node are added to the map.
 */
function collectSubtreeMetrics(
	node: unknown,
	attrFingerprints: Map<string, number>,
	topLevelLocalId: string | undefined,
	nestedToTopLevel: Map<string, string>,
): {
	descendantCount: number;
	textLength: number;
} {
	if (node === null || typeof node !== 'object') {
		return { textLength: 0, descendantCount: 0 };
	}
	const n = node as {
		attrs?: unknown;
		content?: unknown[];
		marks?: unknown;
		text?: unknown;
		type?: unknown;
	};
	let textLength = 0;
	let descendantCount = 1; // this node + its descendants below
	if (n.type === 'text' && typeof n.text === 'string') {
		textLength += n.text.length;
	} else if (typeof n.type === 'string') {
		const attrs =
			n.attrs !== null && typeof n.attrs === 'object'
				? (n.attrs as Record<string, unknown>)
				: undefined;
		textLength += getAtomicInlineNodeTextLength(n.type, attrs);
	}
	const fingerprint = buildNodeAttrFingerprint(n);
	attrFingerprints.set(fingerprint, (attrFingerprints.get(fingerprint) ?? 0) + 1);
	if (Array.isArray(n.content)) {
		for (const child of n.content) {
			// Record the child's localId → top-level ancestor before recursing.
			// We do this here (at the child level, not the node level) so that
			// the top-level node itself is never added as its own descendant.
			if (topLevelLocalId !== undefined && child !== null && typeof child === 'object') {
				const childNode = child as { attrs?: { localId?: unknown } };
				const childLocalId =
					typeof childNode.attrs?.localId === 'string' ? childNode.attrs.localId : undefined;
				if (childLocalId !== undefined) {
					nestedToTopLevel.set(childLocalId, topLevelLocalId);
				}
			}
			const childMetrics = collectSubtreeMetrics(
				child,
				attrFingerprints,
				topLevelLocalId,
				nestedToTopLevel,
			);
			textLength += childMetrics.textLength;
			descendantCount += childMetrics.descendantCount;
		}
	}
	return { textLength, descendantCount };
}

/**
 * Iterate over a list of tool-call records and collect every localId that
 * was named as an explicit target.
 */
export function collectAffectedLocalIds(toolCalls: ToolCallRecord[]): Set<string> {
	const ids = new Set<string>();
	for (const record of toolCalls) {
		if (record.targetLocalId !== undefined) {
			ids.add(record.targetLocalId);
		}
		if (Array.isArray(record.targetLocalIds)) {
			for (const id of record.targetLocalIds) {
				ids.add(id);
			}
		}
	}
	return ids;
}

/**
 * Returns a map from every **nested** node's `localId` → the `localId` of
 * the top-level node that contains it. Top-level nodes themselves are NOT
 * keyed (they ARE the ancestor).
 *
 * This is used by `analyzeOutcome` to resolve nested tool-call targets to
 * their top-level container so that the container's localId can be added to
 * `affectedLocalIds`. Without this, a `replaceNode` targeting a paragraph
 * inside a table cell would leave the table's localId out of
 * `affectedLocalIds`, causing the analyzer to flag the table as an
 * "unintentionally modified" region even though the edit was fully
 * intentional.
 *
 * Internally this is a thin wrapper over `snapshotDoc` — the map is built
 * as a **free byproduct** of the single `collectSubtreeMetrics` walk that
 * `snapshotDoc` already performs. No additional document traversal occurs.
 *
 * Exported for unit testing only. Production callers should use
 * `analyzeOutcome`, which reads `pre.nestedToTopLevel` directly.
 *
 */
export function buildNestedToTopLevelMap(doc: DocLike): Map<string, string> {
	return snapshotDoc(doc).nestedToTopLevel;
}

export interface AnalyzeOutcomeArgs {
	postSessionAdf: DocLike;
	preSessionAdf: DocLike;
	/**
	 * From the backend streaming payload. Optional today (the FE upgrade to consume
	 * this is tracked by CCI-15978). When supplied, will be merged with
	 * `affectedLocalIds` so the "intended region" includes both the
	 * backend-declared selection and what the tool calls actually touched.
	 */
	selectionLocalIds?: string[];
	toolCalls: ToolCallRecord[];
}

/**
 * Compute a structural-delta summary of what happened between
 * `preSessionAdf` and `postSessionAdf`, classified by whether the change
 * happened inside or outside the model's intended target region.
 *
 * The analyzer is intentionally cheap — it walks top-level children only and
 * does not do a deep tree diff. That keeps it suitable for end-of-session
 * invocation without slowing down user-perceived latency, while still
 * surfacing the most consequential anomalies (top-level node removed, top-
 * level node text content significantly changed).
 *
 * `protectedContentRemoved` is currently always `false`; the heuristic that
 * fills it in depends on backend-supplied `selectionLocalIds` which is not
 * yet plumbed end-to-end. See the follow-up ticket for the implementation.
 */

/**
 * Categories of session intent inferred from the mix of tool-call types
 * actually applied during the session.
 *
 * - `'append'`   — every applied call is purely additive (`appendNode`,
 *                  `appendNodeToEnd`, `insertNodeAfter`, `insertNodeBefore`).
 *                  Net node delta is expected to be `≥ 0`.
 * - `'rewrite'`  — every applied call is in-place (`replaceNode`, `setAttrs`,
 *                  `wrap`, `wrapNode`). Net node delta is expected to be
 *                  small (`|delta| ≤ 1` accounting for ProseMirror
 *                  normalisation).
 * - `'remove'`   — every applied call is destructive (`removeNode`,
 *                  `removeNodes`, `moveNode`). Net node delta is expected
 *                  to be `< 0`.
 * - `'mixed'`    — multiple intent categories present, OR no recognised
 *                  command types ran. The intent-effect check is skipped
 *                  for this category because the heuristic would be too
 *                  noisy to be useful.
 */
type SessionIntent = 'append' | 'rewrite' | 'remove' | 'mixed';

const APPEND_INTENT_COMMANDS: ReadonlySet<string> = new Set([
	'append',
	'appendNode',
	'appendNodeToEnd',
	'insertNodeAfter',
	'insertNodeBefore',
]);

const REWRITE_INTENT_COMMANDS: ReadonlySet<string> = new Set([
	'replaceNode',
	'setAttrs',
	'wrap',
	'wrapNode',
]);

const REMOVE_INTENT_COMMANDS: ReadonlySet<string> = new Set([
	'removeNode',
	'removeNodes',
	'moveNode',
]);

/**
 * Infer the session's intent from the mix of tool-call types actually applied.
 *
 * Conservative — returns `'mixed'` whenever the call list spans more than
 * one intent category, or when no recognised command types ran. The caller
 * should treat `'mixed'` as "skip the intent-effect check".
 */
// Minimum fraction of pre-session text that must survive a replaceDoc
// operation before `unexpected_content_removal` is raised.
//
// Validated against 11 production examples (CCI-16970):
//   7 false positives all had ≥96% text retention (well above this threshold)
//   4 true positives all had ≤52% text retention (well below this threshold)
// A 70% threshold cleanly separates the two populations with comfortable margin.
export const REPLACE_DOC_TEXT_RETENTION_THRESHOLD = 0.7;

function inferSessionIntent(toolCalls: readonly ToolCallRecord[]): SessionIntent {
	let hasAppend = false;
	let hasRewrite = false;
	let hasRemove = false;
	for (const call of toolCalls) {
		if (APPEND_INTENT_COMMANDS.has(call.commandType)) {
			hasAppend = true;
		} else if (REWRITE_INTENT_COMMANDS.has(call.commandType)) {
			hasRewrite = true;
		} else if (REMOVE_INTENT_COMMANDS.has(call.commandType)) {
			hasRemove = true;
		}
	}
	const categoryCount = (hasAppend ? 1 : 0) + (hasRewrite ? 1 : 0) + (hasRemove ? 1 : 0);
	if (categoryCount !== 1) {
		// Either zero recognised categories (no signal) or two-or-more
		// (genuinely mixed — heuristic too noisy to apply).
		return 'mixed';
	}
	if (hasAppend) {
		return 'append';
	}
	if (hasRewrite) {
		return 'rewrite';
	}
	return 'remove';
}

/**
 * Decide whether the top-level structural delta contradicts the inferred
 * intent. Uses the **top-level** node delta (not recursive) because the
 * heuristic targets root-shape contradictions and its `±1` tolerance was
 * tuned to the top-level scale.
 *
 * Threshold rationale:
 * - `'append'`  — any net removal (`topLevelNodeDelta < 0`) is a mismatch.
 *                  Net delta of `0` is allowed: a `replaceNode`-shaped
 *                  command can produce zero delta even with append intent
 *                  (this case is rare but not a contradiction).
 * - `'rewrite'` — `|topLevelNodeDelta| ≥ 2` is a mismatch. The `±1` window
 *                  absorbs single trailing whitespace / newline / paragraph
 *                  normalisation that can shift the top-level count by 1
 *                  without indicating a real structural change.
 * - `'remove'`  — `topLevelNodeDelta ≥ 0` is a mismatch (the apply layer
 *                  didn't actually remove anything despite remove intent).
 */
/**
 * Returns true when the session's inferred intent contradicts the observed
 * structural delta — a heuristic signal that something unexpected happened.
 *
 * @param intent - Inferred from the mix of tool-call types in the session.
 * @param topLevelNodeDelta - post minus pre top-level node count.
 * @param totalNodeDelta - post minus pre recursive node count (CCI-16969 P1).
 * @param anyAffectedLocalIdDisappearedPostApply - true when a targeted top-level
 *   node is absent from the post-doc (CCI-16969 P2): a replaceNode that expands
 *   one container into many top-level nodes produces a large topLevelNodeDelta
 *   that is intentional, not anomalous.
 */
function detectIntentEffectMismatch(
	intent: SessionIntent,
	topLevelNodeDelta: number,
	totalNodeDelta: number,
	anyAffectedLocalIdDisappearedPostApply: boolean,
): boolean {
	switch (intent) {
		case 'append':
			return topLevelNodeDelta < 0;
		case 'rewrite':
			// CCI-16969 P2: when the targeted node itself disappeared from the
			// post-doc, the expansion is intentional (e.g. bulletList → headed
			// sections, expand → inline content). Skip the topLevelDelta check.
			if (anyAffectedLocalIdDisappearedPostApply) {
				return false;
			}
			return Math.abs(topLevelNodeDelta) >= 2;
		case 'remove':
			// CCI-16969 P1: removeNodes targeting nested nodes (e.g. tableRows
			// inside a table) leaves topLevelNodeDelta at 0 even though content
			// was legitimately removed. Use recursive totalNodeDelta instead,
			// which correctly captures nested removals.
			return totalNodeDelta >= 0;
		case 'mixed':
		default:
			return false;
	}
}

export function analyzeOutcome({
	preSessionAdf,
	postSessionAdf,
	toolCalls,
	selectionLocalIds,
}: AnalyzeOutcomeArgs): OutcomeAnalysis {
	const pre = snapshotDoc(preSessionAdf);
	const post = snapshotDoc(postSessionAdf);

	const affectedLocalIds = collectAffectedLocalIds(toolCalls);
	if (Array.isArray(selectionLocalIds)) {
		for (const id of selectionLocalIds) {
			affectedLocalIds.add(id);
		}
	}

	// For any targeted localId that belongs to a nested node
	// (e.g. a paragraph inside a table cell), also add the top-level
	// ancestor's localId to `affectedLocalIds`. Without this, the
	// modification-detection loop below flags the top-level container
	// (table, panel, expand, etc.) as "unintentionally modified" even
	// though the change was entirely within the tool call's intended target.
	//
	// `pre.nestedToTopLevel` is built as a free byproduct of the
	// `snapshotDoc` walk above — no additional document traversal.
	// Snapshot the set before iterating so that newly added ancestor IDs are
	// not re-examined. Mutating a Set during for-of is valid JS (new entries
	// ARE visited), but snapshotting makes the intent explicit and removes the
	// dependency on that spec detail.
	for (const targetId of [...affectedLocalIds]) {
		const topLevelAncestorId = pre.nestedToTopLevel.get(targetId);
		if (topLevelAncestorId !== undefined) {
			affectedLocalIds.add(topLevelAncestorId);
		}
	}

	/**
	 * True when at least one targeted localId is absent from the post-doc.
	 *
	 * This happens when `replaceNode` (or similar) creates replacement nodes
	 * with fresh localIds — the targeted node has been genuinely replaced, not
	 * silently dropped. Using this guard prevents two classes of false positives:
	 *
	 * - CCI-16968: `noMeaningfulEffect` fires because `totalNodeDelta === 0`
	 *   and `|textCharacterDelta| <= 1`, even though real content was rewritten
	 *   (the targeted node's content is now under a different localId).
	 *
	 * - CCI-16969 (P2): `intentEffectMismatch` fires for 'rewrite' intent when
	 *   `|topLevelNodeDelta| >= 2` because one container was replaced by many
	 *   top-level nodes (e.g. a bulletList expanded into headed sections). The
	 *   large top-level delta is intentional — the targeted node is gone and new
	 *   nodes took its place.
	 *
	 * Known limitation: `pre.byLocalId` contains only top-level nodes, so this
	 * check does not fire when a `replaceNode` targets a nested node (e.g. a
	 * paragraph inside a table cell) and produces fresh localIds. In practice
	 * the other `noMeaningfulEffect` conditions (`|textCharacterDelta| <= 1`)
	 * rarely hold simultaneously for nested rewrites, so the gap is low-impact.
	 */
	const anyAffectedLocalIdDisappearedPostApply = [...affectedLocalIds].some(
		(id) => pre.byLocalId.has(id) && !post.byLocalId.has(id),
	);

	// CCI-16970: replaceDoc replaces the entire document with HTML-streamed
	// content that goes through an HTML→ADF parser, which always assigns fresh
	// localIds to every node. As a result:
	//   • All pre-session localIds disappear from the post-doc, regardless of
	//     how much content the model actually preserved.
	//   • `affectedLocalIds` is always empty for replaceDoc (it has no
	//     `targetLocalId`).
	// Running the localId-based removal loop below would therefore flag every
	// pre-doc node as "unintendedly removed" on every replaceDoc session —
	// a guaranteed false positive even when the model faithfully rewrote the
	// entire document.
	//
	// Instead, for replaceDoc sessions we skip the localId loops entirely and
	// use a proportional text-loss check: the model genuinely truncated the
	// document only when the post-doc retains less than 70% of the pre-doc
	// text length.
	//
	// Threshold validation: tested against 11 production examples —
	//   7 false positives all had ≥96% text retention (well above 70%)
	//   4 true positives all had ≤52% text retention (well below 70%)
	// A 70% threshold cleanly separates the two populations.
	const hasReplaceDoc = toolCalls.some((tc) => tc.commandType === 'replaceDoc');

	let removedNodeCount = 0;
	let unintendedRegionRemoved = false;

	if (hasReplaceDoc) {
		// Proportional text-loss check for replaceDoc sessions.
		// Only meaningful when the pre-doc had text to begin with; empty
		// documents are silently skipped to avoid division-by-zero edge cases.
		if (
			pre.totalTextLength > 0 &&
			post.totalTextLength < pre.totalTextLength * REPLACE_DOC_TEXT_RETENTION_THRESHOLD
		) {
			unintendedRegionRemoved = true;
			// Use the pre-doc top-level count so dashboards can see how many
			// nodes were effectively dropped (the post-doc nodes are all new).
			removedNodeCount = pre.totalNodesTopLevel;
		}
	} else {
		for (const [localId] of pre.byLocalId) {
			if (!post.byLocalId.has(localId)) {
				removedNodeCount += 1;
				if (!affectedLocalIds.has(localId)) {
					unintendedRegionRemoved = true;
				}
			}
		}

		// Account for unlabelled top-level nodes (no `localId` attr).
		// These can't be targeted by any tool call (every command keys off
		// `localId`), so a net drop in unlabelled-node count is by definition
		// unintended collateral damage. Tracking it here keeps
		// `removedNodeCount` consistent with `totalNodeDelta` and surfaces
		// the most clearly-anomalous removals on the `unintendedRegionRemoved`
		// flag.
		//
		// Known limitation: an equal-count swap (one unlabelled node removed
		// AND one unlabelled node added in the same session) is invisible to
		// this counter. Detecting it would require position/content matching,
		// which the analyzer intentionally avoids for cost reasons. Net delta
		// is sufficient for current dashboard needs.
		// Unlabelled-node accounting uses the top-level count: only top-level
		// nodes are keyed in `byLocalId`, so the comparison must be against
		// `totalNodesTopLevel` to keep the subtraction meaningful.
		const preUnlabelledCount = pre.totalNodesTopLevel - pre.byLocalId.size;
		const postUnlabelledCount = post.totalNodesTopLevel - post.byLocalId.size;
		// CCI-17328: appending labelled content to an empty starting document
		// (a single unlabelled placeholder paragraph) drops that placeholder.
		// That is a benign, expected removal — not content loss — so do not
		// count it towards `removedNodeCount` / `unintendedRegionRemoved`.
		const benignEmptyDocPlaceholderRemoval = isEmptyStartingDocument(preSessionAdf);
		if (postUnlabelledCount < preUnlabelledCount && !benignEmptyDocPlaceholderRemoval) {
			const unlabelledRemoved = preUnlabelledCount - postUnlabelledCount;
			removedNodeCount += unlabelledRemoved;
			unintendedRegionRemoved = true;
		}
	}

	// Modification detection uses total text-length inequality as a cheap
	// proxy for "this node's text changed". A `±1` character tolerance is
	// applied per node to absorb ProseMirror normalisation artefacts
	// (single trailing whitespace, newline, punctuation injection/strip
	// on block close) — same rationale as the session-wide
	// `noMeaningfulEffect` derivation. Same-length rewrites are still a
	// silent false negative — see the JSDoc on
	// `OutcomeAnalysis.unintendedRegionModified` for the known limitation.
	let unintendedRegionModified = false;
	for (const [localId, preRecord] of pre.byLocalId) {
		const postRecord = post.byLocalId.get(localId);
		if (postRecord === undefined) {
			continue;
		}
		if (
			Math.abs(preRecord.textLength - postRecord.textLength) > 1 &&
			!affectedLocalIds.has(localId)
		) {
			unintendedRegionModified = true;
			break;
		}
	}

	// Recursive count for `noMeaningfulEffect` — catches depth-only changes
	// (blank column added, empty rows removed, nested reorders). See CCI-16816.
	const totalNodeDelta = post.totalNodesRecursive - pre.totalNodesRecursive;
	// Top-level count for `intentEffectMismatch` — the heuristic's ±1
	// tolerance was tuned to top-level scale; recursive would be too noisy.
	const topLevelNodeDelta = post.totalNodesTopLevel - pre.totalNodesTopLevel;
	const intent = inferSessionIntent(toolCalls);
	const intentEffectMismatch = detectIntentEffectMismatch(
		intent,
		topLevelNodeDelta,
		totalNodeDelta,
		anyAffectedLocalIdDisappearedPostApply,
	);

	// Multiset equality on per-node fingerprints. Catches attribute-only
	// changes and compositional swaps that node count + text length miss.
	let attrSignaturesEqual = pre.attrFingerprints.size === post.attrFingerprints.size;
	if (attrSignaturesEqual) {
		for (const [fingerprint, preCount] of pre.attrFingerprints) {
			if (post.attrFingerprints.get(fingerprint) !== preCount) {
				attrSignaturesEqual = false;
				break;
			}
		}
	}

	return {
		totalNodeDelta,
		removedNodeCount,
		textCharacterDelta: post.totalTextLength - pre.totalTextLength,
		affectedLocalIds,
		unintendedRegionModified,
		unintendedRegionRemoved,
		protectedContentRemoved: false,
		intentEffectMismatch,
		attrSignaturesEqual,
		anyAffectedLocalIdDisappearedPostApply,
	};
}
